// Pallets and To palletise (CONTROL V4 sd-module plRows / plTo / palFacts /
// createPallets). The shapes are the API's (routes/pallets.js); the wording
// and the planning rules are the prototype's. The rules here only drive what
// the panel shows as you type: the API applies the same ones again and is
// the authority, so a stale screen can never create a pallet it refuses.

export interface Ref {
  id: string
  name: string
}

export interface PalletSource {
  id: string
  variety: string
  box_type: string
  harvest_date: string | null
  site_name: string | null
  contractor_name: string | null
  lid_name?: string | null
  hold_active?: boolean
}

export interface PalletOrder {
  id: string
  reference: string
  approval_status: string
  customer_name: string | null
  line_id: string
  variety: string
  box_type: string
}

export interface Pallet {
  id: string
  pallet_id: string
  source: PalletSource
  planned_boxes: number
  confirmed_boxes: number | null
  boxes: number
  capacity: number
  partial: boolean
  state: 'planned' | 'confirmed' | 'discrepancy'
  hold: { reason: string; by: string; at: string } | null
  applied_lid: Ref | null
  required_lid: Ref | null
  line_lid: Ref | null
  location: Ref | null
  order: PalletOrder | null
  dispatch: { id: string; reference: string; state: string } | null
  departed: boolean
  cancelled: boolean
  note?: { text: string; by: string | null; at: string | null } | null
  created_at: string
}

export interface PalletEvent {
  id: string
  type: string
  at: string
  what: string
  by: string
  how: string
  reason: string | null
}

export interface PalletDetail extends Pallet {
  gates: string[]
  history: PalletEvent[]
}

export interface ToPalletiseRow {
  source: PalletSource
  remaining: number
  capacity: number
  equivalent: number
  orders: string[]
}

export interface OrderOption {
  id: string
  reference: string
  approval_status: string
  customer_name: string | null
  lines: { id: string; variety: string; box_type: string; outstanding: number; lid: Ref | null }[]
}

export interface PlanResponse {
  source: PalletSource & { lid: Ref | null }
  capacity: number
  eligible: number
  reservation: {
    allocation_id: string
    line_id: string
    order_id: string
    order_reference: string
    customer_name: string | null
    approval_status: string
    lid_id: string | null
    lid: Ref | null
  } | null
  lids: Ref[]
  locations: Ref[]
  orders: OrderOption[]
}

export const APPROVAL_LABEL: Record<string, string> = {
  draft: 'Draft',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  returned_for_changes: 'Returned for changes',
  reapproval_required: 'Reapproval required',
}

// V4 PSTATE: label, background, ink.
export const PSTATE: Record<Pallet['state'], [string, string, string]> = {
  planned: ['Planned', '#f2ece1', '#5c5449'],
  confirmed: ['Confirmed', '#e8efd6', '#3f4a2c'],
  discrepancy: ['Discrepancy', '#fbe4dc', '#8a2f1d'],
}

export const HOLD_CHIP: [string, string, string] = ['On hold', '#fbe4dc', '#8a2f1d']

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// V4 fmtDS: "20 Sep". Parsed from the date's own parts, never through UTC.
export function fmtDay(d: string | null | undefined): string {
  if (!d) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d)
  if (!m) return '—'
  return parseInt(m[3], 10) + ' ' + MONTHS[parseInt(m[2], 10) - 1]
}

// V4 fmtStamp: "20 Sep · 08:33", local time.
export function fmtStamp(iso: string | null | undefined): string {
  if (!iso) return ''
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return ''
  return t.getDate() + ' ' + MONTHS[t.getMonth()] + ' · ' + String(t.getHours()).padStart(2, '0') + ':' + String(t.getMinutes()).padStart(2, '0')
}

export const srcTitle = (s: PalletSource) => s.variety + ' · ' + s.box_type
export const srcShort = (s: PalletSource) =>
  (s.site_name || '—') + ' / ' + (s.contractor_name || '—') + ' / ' + fmtDay(s.harvest_date)
export const srcLabel = (s: PalletSource) =>
  [s.variety, s.box_type, s.site_name || '—', s.contractor_name || '—', fmtDay(s.harvest_date)].join(' · ')

export const plural = (n: number, one: string, many = one + 's') => n + ' ' + (n === 1 ? one : many)

// ---------------------------------------------------------------------------
// Register
// ---------------------------------------------------------------------------

export type StateFilter = '' | 'planned' | 'confirmed' | 'discrepancy' | 'held' | 'partial'

export const STATE_FILTERS: [StateFilter, string][] = [
  ['', 'Any state'],
  ['planned', 'Planned'],
  ['confirmed', 'Confirmed'],
  ['discrepancy', 'Discrepancy'],
  ['held', 'On hold'],
  ['partial', 'Partial pallets'],
]

export function filterPallets(ps: Pallet[], state: StateFilter, q: string): Pallet[] {
  let out = ps
  if (state === 'held') out = out.filter((p) => !!p.hold)
  else if (state === 'partial') out = out.filter((p) => p.partial)
  else if (state) out = out.filter((p) => p.state === state)
  const needle = q.trim().toLowerCase()
  if (needle) {
    out = out.filter((p) =>
      [p.pallet_id, p.order?.reference || '', srcLabel(p.source)].join(' ').toLowerCase().includes(needle)
    )
  }
  return out
}

// V4 plRows boxes: "96 planned" / "90 confirmed", "· partial" under capacity.
export function boxesText(p: Pallet): string {
  const base = p.confirmed_boxes == null ? p.planned_boxes + ' planned' : p.confirmed_boxes + ' confirmed'
  return base + (p.partial ? ' · partial' : '')
}

// "Applied / required". Red only when an order line's lid is not the one on
// the pallet: that is what blocks loading. An unassigned pallet's own
// requirement is shown but is not a departure gate.
export function lidText(p: Pallet): string {
  return (p.applied_lid?.name || 'none') + ' / ' + (p.required_lid?.name || 'none')
}
export const lidWrong = (p: Pallet) => !!p.line_lid && p.applied_lid?.id !== p.line_lid.id

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

export function prepBlock(order: { reference: string; approval_status: string } | null | undefined): string {
  if (!order || order.approval_status === 'approved') return ''
  const label = APPROVAL_LABEL[order.approval_status] || order.approval_status
  return `Preparation release needs an approved order: ${order.reference} is ${label.toLowerCase()}.`
}

export const DSTATE: Record<string, string> = {
  draft: 'Draft',
  ready_to_load: 'Ready to load',
  loading: 'Loading',
  dispatched: 'Dispatched',
  cancelled: 'Cancelled',
}

export function facts(p: PalletDetail): { k: string; v: string }[] {
  const o = p.order
  const rows: [string, string][] = [
    ['Source', srcLabel(p.source)],
    ['Capacity', p.capacity + ' boxes (job snapshot)'],
    ['Planned', p.planned_boxes + ' boxes'],
    ['Confirmed', p.confirmed_boxes == null ? 'Not yet confirmed' : p.confirmed_boxes + ' boxes'],
    ['Order / line', o ? o.reference + ' · ' + o.variety + ' ' + o.box_type : 'Not assigned'],
    ['Required lid', p.required_lid?.name || 'Not specified'],
    ['Applied lid', p.applied_lid?.name || 'None recorded'],
    ['Location', p.location?.name || 'Not recorded'],
    ['Load', p.dispatch ? p.dispatch.reference + ' · ' + (DSTATE[p.dispatch.state] || p.dispatch.state) : 'None'],
    ['Departed', p.departed ? 'Yes' : 'No'],
  ]
  return rows.map(([k, v]) => ({ k, v }))
}

// Which actions the drawer offers (V4 palCan*). This is state only: it says what
// the pallet's state allows. Who may act is applied by the caller (PalletDrawer
// reads Manage stock & dispatch and offers no action without it), and the API
// still refuses anything the pallet's state or the caller's permission forbids.
export function drawerActions(p: PalletDetail) {
  const blocked = p.state === 'planned' ? prepBlock(p.order) : ''
  const req = p.required_lid
  return {
    confirm: !p.departed && p.state === 'planned' && !blocked,
    confirmBlocked: blocked,
    confirmLid: !p.departed && p.state !== 'planned' && !!req && p.applied_lid?.id !== req.id,
    accept: p.state === 'discrepancy',
    move: !p.departed,
    hold: !p.departed && !p.hold,
    release: !!p.hold,
    cancel: p.state === 'planned' && !p.departed,
  }
}

// ---------------------------------------------------------------------------
// Create pallets
// ---------------------------------------------------------------------------

// V4 suggest(): capacity-sized pallets, remainder last.
export function suggest(total: number, cap: number): string[] {
  if (!(total > 0) || !(cap > 0)) return ['']
  const out: string[] = []
  let left = total
  while (left > 0) {
    out.push(String(Math.min(cap, left)))
    left -= cap
  }
  return out
}

export const digits = (v: string) => v.replace(/[^0-9]/g, '')
const int = (v: string) => parseInt(v, 10) || 0

export function planErrors(qty: string, parts: string[], eligible: number, capacity: number, fromAllocation: boolean): string[] {
  const total = int(qty)
  const ns = parts.map(int)
  const sum = ns.reduce((a, x) => a + x, 0)
  const errs: string[] = []
  if (total <= 0) errs.push('Enter a whole number of boxes.')
  if (total > eligible) errs.push(`Only ${eligible} boxes are eligible${fromAllocation ? ' on this reservation' : ''}.`)
  if (ns.some((x) => x <= 0)) errs.push('Every pallet needs a positive count.')
  if (ns.some((x) => x > capacity)) errs.push(`A pallet cannot exceed capacity ${capacity}.`)
  if (total > 0 && sum !== total) errs.push(`Pallets sum to ${sum}, not ${total}.`)
  return errs
}

// The orders a pallet from this group can be planned against: V4 cpOrderOpts
// (not draft, something outstanding) narrowed to a line for this variety and
// box type. The API has already dropped draft orders and settled lines.
export function ordersFor(orders: OrderOption[], s: PalletSource) {
  const out: { order: OrderOption; line: OrderOption['lines'][number] }[] = []
  for (const o of orders) {
    const line = o.lines.find((l) => l.variety === s.variety && l.box_type === s.box_type)
    if (line) out.push({ order: o, line })
  }
  return out
}

export function confirmMismatch(qty: string, planned: number): string {
  const q = int(qty)
  return q > 0 && q !== planned
    ? `Confirming ${q} against ${planned} planned records a discrepancy for Control to resolve. The order and source are not changed and the pallet cannot depart until resolved.`
    : ''
}
